import { useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";

import { useAuthedApi } from "@/api/useAuthedApi";
import { useOnboardingState } from "./useOnboardingState";

const INVITE_ERROR_MAP: Record<string, string> = {
  expired: "This invite has expired.",
  used: "This invite has already been used.",
  email_mismatch: "This invite was sent to a different email.",
  already_member: "You're already in a company.",
  "already in a company": "You're already in a company.",
};

function friendlyError(msg: string): string {
  return INVITE_ERROR_MAP[msg] ?? INVITE_ERROR_MAP[msg.toLowerCase()] ?? msg;
}

/** Step 2: Create or join a company workspace.
 *  On success, flushes buffered context then hard-navigates so RequireAuth re-reads /auth/me. */
export function WorkspaceStep() {
  const { companies, onboarding } = useAuthedApi();
  const { industry, role, intents } = useOnboardingState();

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [seatError, setSeatError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function flushContext() {
    try {
      await onboarding.postContext({ industry, role, intents });
    } catch {
      // Non-fatal: context flush failure doesn't block onboarding
    }
  }

  function finish() {
    // Hard navigation (not react-router navigate) so RequireAuth remounts and
    // re-fetches /auth/me. `needs_onboarding` is read once at mount and cached
    // in guard state — a client-side navigate keeps the stale `true` from the
    // pre-company session and bounces the user straight back to /onboard.
    // The membership row is already committed server-side, so the fresh
    // /auth/me returns needs_onboarding:false and onboarding_step advances.
    window.location.assign("/onboard");
  }

  async function onCreate() {
    setError(null);
    setSeatError(null);
    setBusy(true);
    try {
      await companies.createCompany(name.trim());
      await flushContext();
      finish();
    } catch (e) {
      setError(friendlyError((e as Error).message || "Could not create company"));
    } finally {
      setBusy(false);
    }
  }

  async function onJoin() {
    setError(null);
    setSeatError(null);
    setBusy(true);
    try {
      await companies.join(code.trim());
      await flushContext();
      finish();
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string };
      if (err.status === 402) {
        setSeatError(
          "This workspace is full. Ask an admin to upgrade the plan or free up a seat.",
        );
      } else {
        setError(friendlyError(err.message || "Could not join with that code"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Set up your workspace
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {seatError && (
        <Alert severity="warning">
          {seatError}
          <Button size="small" sx={{ ml: 1 }} onClick={() => setSeatError(null)}>
            Create your own instead
          </Button>
        </Alert>
      )}

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Create a new company</Typography>
        <TextField
          label="Company name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
        />
        <Button
          variant="contained"
          disabled={busy || !name.trim()}
          onClick={onCreate}
        >
          Create company
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" align="center">
        or
      </Typography>

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Join with an invite code</Typography>
        <TextField
          label="Invite code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          fullWidth
          size="small"
        />
        <Button
          variant="outlined"
          disabled={busy || !code.trim()}
          onClick={onJoin}
        >
          Join
        </Button>
      </Stack>
    </Stack>
  );
}
